import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  BusinessCenter,
  Description,
  Groups,
  Hub,
  Inventory2,
  Launch,
  ManageSearch,
  Science,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

type IctTab =
  | 'sobre'
  | 'governanca'
  | 'politicas'
  | 'grupos-linhas'
  | 'pesquisadores'
  | 'laboratorios'
  | 'projetos'
  | 'nit'
  | 'publicacoes-pdi'
  | 'eventos'
  | 'agenda-eventos'
  | 'inscricoes-informacoes';

interface IctTabItem {
  id: IctTab;
  label: string;
  children?: IctTabItem[];
}

interface IctNavigationItem extends IctTabItem {
  depth: number;
}

interface LaboratoryEnvironment {
  title: string;
  description: string;
  resources?: string[];
  applicationsTitle?: string;
  applications?: string[];
}

const tabs: IctTabItem[] = [
  { id: 'sobre', label: 'Sobre' },
  { id: 'governanca', label: 'Governança' },
  { id: 'politicas', label: 'Políticas Institucionais' },
  { id: 'grupos-linhas', label: 'Grupos e Linhas de Pesquisa' },
  { id: 'pesquisadores', label: 'Pesquisadores' },
  { id: 'laboratorios', label: 'Infraestrutura e Laboratórios' },
  { id: 'projetos', label: 'Projetos' },
  { id: 'nit', label: 'NIT' },
  { id: 'publicacoes-pdi', label: 'Publicações PDI' },
  {
    id: 'eventos',
    label: 'Eventos',
    children: [
      { id: 'agenda-eventos', label: 'Agenda e Eventos' },
      { id: 'inscricoes-informacoes', label: 'Inscrições e Informações' },
    ],
  },
];

const navigationItems: IctNavigationItem[] = tabs.flatMap((tab) => [
  { ...tab, depth: 0 },
  ...(tab.children?.map((child) => ({ ...child, depth: 1 })) ?? []),
]);

const tabIds = navigationItems.map((tab) => tab.id);

const aboutParagraphs = [
  'O Instituto de Ciência e Tecnologia do Senac Amazonas (ICT Senac AM) é uma unidade vinculada à Faculdade de Tecnologia Senac Amazonas, criada com o propósito de promover a pesquisa, o desenvolvimento tecnológico, a inovação e a transferência de conhecimento para a sociedade.',
  'Atuando de forma integrada ao ecossistema regional de inovação, o ICT Senac AM desenvolve projetos de Pesquisa, Desenvolvimento e Inovação (PD&I) voltados à geração de soluções tecnológicas, à transformação digital de organizações e ao fortalecimento da competitividade dos setores de comércio, serviços e turismo.',
  'Por meio da articulação entre pesquisadores, docentes, estudantes, empresas, órgãos governamentais e instituições parceiras, o Instituto busca transformar conhecimento em soluções práticas, contribuindo para o desenvolvimento sustentável da Amazônia e para o avanço científico e tecnológico da região.',
  'Suas atividades abrangem áreas estratégicas como Inteligência Artificial, Ciência de Dados, Transformação Digital, Business Intelligence, Internet das Coisas (IoT), Inovação Tecnológica, Desenvolvimento de Software, Inteligência Econômica, Saúde Digital e Tecnologias Aplicadas ao Comércio, Serviços e Turismo.',
];

const values = [
  'Inovação e excelência científica',
  'Transparência e Governança',
  'Sustentabilidade',
  'Impacto social',
  'Colaboração e Protagonismo',
];

const objectives = [
  'Consolidar-se como Referência em Tecnologia da Informação e IA para o Segmento de Comércio de Bens, Serviços e Turismo no Amazonas.',
  'Fortalecer Parcerias Estratégicas com o Ecossistema de Inovação Local, Nacional e Internacional.',
  'Promover a Transferência de Tecnologia e Conhecimento em IA para o Segmento de Comércio de Bens, Serviços e Turismo.',
  'Assegurar a Sustentabilidade e a Governança do ICT, com uma Gestão Eficiente e Transparente.',
];

const historyParagraphs = [
  'O Instituto de Ciência e Tecnologia do Senac Amazonas (ICT Senac AM) foi criado como uma iniciativa estratégica do Serviço Nacional de Aprendizagem Comercial – Senac Amazonas, com o objetivo de fortalecer a atuação institucional em pesquisa aplicada, desenvolvimento tecnológico e inovação.',
  'Sua criação está alinhada à evolução da Faculdade de Tecnologia Senac Amazonas e à crescente necessidade de desenvolver soluções inovadoras capazes de responder aos desafios econômicos, sociais e tecnológicos da região amazônica.',
  'Desde sua implantação, o Instituto tem direcionado seus esforços para a construção de um ambiente favorável à geração de conhecimento, ao desenvolvimento de tecnologias e à aproximação entre a academia e o setor produtivo. Entre suas áreas prioritárias de atuação destacam-se a transformação digital, inteligência artificial, ciência de dados, inovação aplicada aos negócios, inteligência econômica territorial, saúde digital e tecnologias sustentáveis.',
  'Atualmente, o ICT Senac AM desenvolve projetos estratégicos voltados à pesquisa aplicada e à inovação tecnológica, consolidando sua atuação como agente promotor do desenvolvimento científico, tecnológico e da inovação no Estado do Amazonas.',
];

const researchGroupName =
  'Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo – Senac Amazonas – Faculdade de Tecnologia Senac Amazonas (FATESE/AM).';

const researchLines = [
  {
    title: 'Linha 1 – Tecnologias da Informação e Inteligência Artificial',
    description:
      'Esta linha de pesquisa dedica-se ao desenvolvimento e aplicação de tecnologias digitais avançadas, com ênfase em Inteligência Artificial (IA), ciência de dados, automação e Internet das Coisas (IoT), voltadas à transformação digital das atividades do comércio de bens, serviços e turismo. Busca integrar soluções tecnológicas aos processos organizacionais, promovendo eficiência operacional, melhoria na tomada de decisão e inovação em produtos e serviços, alinhando-se às demandas do setor produtivo regional e às tendências da economia digital.',
    objective:
      'Desenvolver e aplicar soluções baseadas em tecnologias da informação e inteligência artificial para promover a inovação, a competitividade e a transformação digital das organizações do setor terciário.',
  },
  {
    title: 'Linha 2 – Biotecnologia',
    description:
      'Esta linha de pesquisa concentra-se no desenvolvimento de soluções biotecnológicas aplicadas ao aproveitamento sustentável da biodiversidade amazônica, com foco na geração de produtos e processos inovadores de alto valor agregado. Abrange estudos em bioeconomia, biotecnologia aplicada à saúde, cosméticos, alimentos e meio ambiente, promovendo a integração entre ciência, tecnologia e sustentabilidade. Busca contribuir para o desenvolvimento regional por meio da inovação baseada em recursos naturais, respeitando princípios ambientais e sociais.',
    objective:
      'Desenvolver soluções biotecnológicas inovadoras, sustentáveis e aplicadas ao uso da biodiversidade amazônica, promovendo o desenvolvimento econômico e tecnológico regional.',
  },
  {
    title: 'Linha 3 – Tecnologias para o Varejo e Experiência do Cliente',
    description:
      'Esta linha investiga e desenvolve soluções tecnológicas e estratégias inovadoras voltadas à modernização do varejo e à melhoria da experiência do cliente. Envolve o uso de ferramentas digitais, plataformas de e-commerce, sistemas omnichannel, análise de comportamento do consumidor e tecnologias emergentes para personalização de serviços. Busca fortalecer a competitividade das empresas do setor terciário por meio da integração entre tecnologia, marketing e gestão, promovendo experiências mais eficientes, personalizadas e centradas no usuário.',
    objective:
      'Desenvolver soluções e modelos inovadores que integrem tecnologia e gestão para aprimorar a experiência do cliente e aumentar a competitividade do varejo e dos serviços.',
  },
  {
    title: 'Linha 4 – Gastronomia e Alimentação do Futuro',
    description:
      'Esta linha de pesquisa aborda tendências emergentes na gastronomia e na alimentação, com foco em inovação, sustentabilidade, tecnologia de alimentos e valorização da cultura alimentar amazônica. Envolve estudos sobre novos modelos de produção e consumo, alimentos funcionais, segurança alimentar, gastronomia sustentável e uso de ingredientes regionais. Também contempla a integração entre ciência, tecnologia e práticas gastronômicas, contribuindo para o desenvolvimento de soluções inovadoras no setor de alimentação e hospitalidade.',
    objective:
      'Investigar e desenvolver soluções inovadoras para o setor de alimentação, integrando tecnologia, sustentabilidade e valorização da cultura alimentar regional.',
  },
];

const infrastructureIntro = [
  'O Instituto de Ciência e Tecnologia do Senac Amazonas (ICT Senac AM) conta com uma infraestrutura multidisciplinar voltada ao desenvolvimento de atividades de Pesquisa, Desenvolvimento e Inovação (PD&I), reunindo ambientes destinados à pesquisa aplicada, inovação, experimentação e desenvolvimento tecnológico.',
  'Os laboratórios e espaços de inovação apoiam projetos relacionados ao desenvolvimento de soluções digitais, novos produtos, processos e serviços, abrangendo áreas como tecnologia, saúde, alimentação, sustentabilidade, experiência do cliente e transformação digital.',
  'A integração entre ambientes tecnológicos e espaços colaborativos favorece a geração de conhecimento, a prototipagem de soluções e a aproximação entre academia, setor produtivo e sociedade, contribuindo para o desenvolvimento de iniciativas inovadoras alinhadas às demandas da Amazônia e dos setores de comércio, serviços e turismo.',
];

const laboratoryEnvironments: LaboratoryEnvironment[] = [
  {
    title: 'Laboratório Microsoft Senac',
    description:
      'Ambiente voltado ao desenvolvimento de soluções tecnológicas, prototipagem, fabricação digital e experiências imersivas, apoiando projetos de pesquisa aplicada e inovação tecnológica.',
    applications: [
      'Inteligência Artificial',
      'Desenvolvimento de Software',
      'Internet das Coisas (IoT)',
      'Realidade Virtual e Aumentada',
      'Ciência de Dados',
    ],
  },
  {
    title: 'Laboratório de Telecomunicações',
    description:
      'Espaço dedicado à pesquisa e ao desenvolvimento de soluções em redes, conectividade, automação, eletrônica aplicada e sistemas embarcados.',
    applications: [
      'Redes de Computadores',
      'Telecomunicações',
      'IoT',
      'Automação',
      'Sistemas Embarcados',
    ],
  },
  {
    title: 'Laboratório de Inovação',
    description:
      'Ambiente destinado à ideação, experimentação e desenvolvimento de projetos inovadores, promovendo a integração entre pesquisa aplicada, tecnologia e empreendedorismo.',
    applications: [
      'Inovação Tecnológica',
      'Design Thinking',
      'Prototipagem',
      'Desenvolvimento de Projetos',
    ],
  },
  {
    title: 'Escritório de Inovação',
    description:
      'Responsável pela articulação institucional, gestão de projetos de PD&I, prospecção tecnológica e apoio às iniciativas de inovação desenvolvidas pelo ICT.',
  },
  {
    title: 'Sala Inovadora',
    description:
      'Espaço colaborativo destinado à realização de oficinas, workshops, mentorias, reuniões técnicas e atividades voltadas ao desenvolvimento de soluções inovadoras.',
  },
  {
    title: 'Laboratório de Informática',
    description:
      'Ambiente utilizado para atividades de ensino, pesquisa e desenvolvimento tecnológico em computação, sistemas de informação e tecnologias digitais.',
  },
  {
    title: 'Clínica Escola de Saúde',
    description:
      'Ambiente que apoia atividades de pesquisa aplicada e inovação em saúde, contribuindo para o desenvolvimento de soluções em saúde digital, inteligência artificial aplicada à saúde e bases de dados para pesquisa científica.',
  },
  {
    title: 'Laboratório de Gastronomia',
    description:
      'Espaço voltado à experimentação, pesquisa aplicada e desenvolvimento de produtos e processos inovadores na área de alimentos e gastronomia.',
  },
];

const projectResearchers = [
  'Anderson Clayton Barreto Rodrigues',
  'Carlos Bruno Oliveira Lopes',
  'José Carlos da Silva Duarte Filho',
  'Edwins Moura Ramires',
  'Nailson Andrade de Souza',
  'Nívea Consuêlo Carvalho dos Santos',
  'Tatiana da Costa Jansen',
  'Yadira Pérez Cutiño',
];

const technologyProjects = [
  {
    title: 'Plataforma de Inteligência em Transformação Digital e Inovação do Comércio (OBTEC Digital)',
    institution: 'ICT SENAC AM',
    group: 'Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo',
    line: 'Tecnologias da Informação e Inteligência Artificial',
    coordinator: 'Samara Castro da Silva',
    researchers: projectResearchers,
    objective:
      'Desenvolver uma plataforma inteligente para diagnóstico, monitoramento e análise da maturidade digital das empresas dos setores de comércio, serviços e turismo do Amazonas, utilizando ciência de dados, Business Intelligence, Inteligência Artificial e indicadores de transformação digital.',
    funding: 'Próprio',
    supportMode: 'Pesquisa Aplicada e Desenvolvimento Tecnológico',
    callNumber: 'Interno',
    technologies: [
      'Inteligência Artificial',
      'Ciência de Dados',
      'Business Intelligence',
      'Computação em Nuvem',
      'Big Data',
      'Transformação Digital',
      'Sistemas Inteligentes',
    ],
    expectedResults: [
      'Índice de Maturidade Digital',
      'Plataforma Analítica',
      'Dashboards Inteligentes',
      'Banco de Dados Territorial',
      'Relatórios Automatizados',
    ],
  },
  {
    title: 'Plataforma de Inteligência Econômica Territorial (OBTEC Econômico)',
    institution: 'ICT SENAC AM',
    group: 'Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo',
    line: 'Tecnologias da Informação e Inteligência Artificial',
    coordinator: 'Samara Castro da Silva',
    researchers: projectResearchers,
    objective:
      'Desenvolver uma plataforma de inteligência econômica territorial capaz de integrar, processar e analisar dados empresariais, econômicos e georreferenciados do Amazonas para subsidiar decisões estratégicas do setor produtivo.',
    funding: 'Próprio',
    supportMode: 'Pesquisa Aplicada e Desenvolvimento Tecnológico',
    callNumber: 'Interno',
    technologies: [
      'Ciência de Dados',
      'Business Intelligence',
      'Geoprocessamento',
      'Big Data',
      'Inteligência Territorial',
      'Computação em Nuvem',
    ],
    expectedResults: [
      'Plataforma de Inteligência Econômica',
      'Dashboards Georreferenciados',
      'Indicadores Territoriais',
      'Sistema de Monitoramento Econômico',
    ],
  },
  {
    title: 'Sistema Inteligente de Cadastro e Contagem Automatizada de Produtos',
    institution: 'ICT SENAC AM',
    group: 'Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo',
    line: 'Tecnologias da Informação e Inteligência Artificial',
    coordinator: 'Samara Castro da Silva',
    researchers: projectResearchers,
    objective:
      'Desenvolver um sistema inteligente para cadastro, rastreamento e conferência automatizada de produtos, reduzindo erros operacionais e aumentando a confiabilidade dos processos de gestão de estoque no comércio atacadista e varejista.',
    funding: 'Próprio',
    supportMode: 'Pesquisa Aplicada e Desenvolvimento Experimental',
    callNumber: 'Interno',
    technologies: [
      'Automação',
      'Sistemas de Gestão',
      'Banco de Dados',
      'Inteligência Artificial',
      'Visão Computacional (evolução futura)',
      'IoT (evolução futura)',
    ],
    expectedResults: ['Software de Gestão de Estoque', 'Sistema de Dupla Validação', 'Aplicativo Mobile', 'Registro de Software'],
  },
  {
    title: 'Sistema Inteligente de Apoio à Tomada de Decisão',
    institution: 'ICT SENAC AM',
    group: 'Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo',
    line: 'Tecnologias da Informação e Inteligência Artificial',
    coordinator: 'Samara Castro da Silva',
    researchers: projectResearchers,
    objective:
      'Desenvolver um sistema de apoio à tomada de decisão baseado em Inteligência Artificial capaz de processar informações empresariais, identificar padrões e gerar recomendações estratégicas para gestores.',
    funding: 'Próprio',
    supportMode: 'Pesquisa Aplicada e Desenvolvimento Tecnológico',
    callNumber: 'Interno',
    technologies: [
      'Inteligência Artificial',
      'Machine Learning',
      'Analytics',
      'Business Intelligence',
      'Sistemas Especialistas',
      'Big Data',
    ],
    expectedResults: ['Plataforma de IA', 'Motor de Recomendação', 'Painéis Inteligentes', 'Modelos Preditivos'],
  },
  {
    title: 'TambaDock – Estação Inteligente Sustentável para Eventos e Espaços Públicos',
    institution: 'ICT SENAC AM',
    group: 'Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo',
    line: 'Tecnologias da Informação e Inteligência Artificial',
    coordinator: 'Samara Castro da Silva',
    researchers: projectResearchers,
    objective:
      'Desenvolver uma estação inteligente sustentável para recarga de dispositivos móveis e monitoramento ambiental, baseada em energia solar, Internet das Coisas (IoT) e sensoriamento digital, voltada para eventos, espaços públicos e ações de sustentabilidade.',
    funding: 'Próprio',
    supportMode: 'Pesquisa Aplicada e Desenvolvimento Experimental',
    callNumber: 'Interno',
    technologies: [
      'Internet das Coisas (IoT)',
      'Energia Solar',
      'Sensoriamento Inteligente',
      'Computação Embarcada',
      'Sustentabilidade Tecnológica',
      'Monitoramento Remoto',
    ],
    expectedResults: [
      'Protótipo Funcional',
      'Sistema de Telemetria',
      'Aplicativo de Monitoramento',
      'Plataforma IoT',
      'Possível Pedido de Patente',
    ],
  },
];

const pdiPublications = [
  {
    title: 'Gestão da Inovação com Ênfase em Pesquisa, Desenvolvimento e Inovação (PD&I)',
    cover: '/assets/ict/publicacoes/gestao-inovacao-pdi.png',
    type: 'E-book',
    description: 'Publicação vinculada ao Programa de Pós graduação lato sensu em Gestão da Inovação com ênfase em Pesquisa, Desenvolvimento e Inovação.',
    organizers: [
      'Nailson Andrade de Souza',
      'Samara Castro da Silva',
      'Yadira Pérez Cutiño',
      'Márcia Cibelle Pontes Batista',
    ],
    doi: '10.29327/5902497',
    isbn: '9786598816117',
    url: 'https://senacam.bnweb.org/bnportal/pt-BR/search/25138?exp=pesquisa',
    tags: ['Gestão da Inovação', 'PD&I'],
  },
  {
    title:
      'Coletânea de Artigos dos Alunos da Pós-Graduação em Gestão da Inovação com Ênfase em Pesquisa, Desenvolvimento e Inovação (PD&I)',
    cover: '/assets/ict/publicacoes/coletanea-artigos-pdi.png',
    type: 'E-book',
    description: 'Coletânea de artigos da pós-graduação, reunindo práticas inovadoras em PD&I.',
    doi: '10.29327/5866904',
    isbn: '9786598816100',
    url: 'https://senacam.bnweb.org/bnportal/pt-BR/search/24432',
    tags: ['Artigos', 'Pós-graduação', 'PD&I'],
  },
];

const eventsAgenda = [
  {
    name: 'Encontro de Gestão da Inovação com Ênfase em PD&I',
    date: '18/08/26',
    place: 'Casarão da Inovação Cassina',
  },
  {
    name: 'III Fórum Acadêmico: Inovação em Tecnologias Forenses e Segurança da Informação',
    date: '18/06/26',
    place: 'Auditório da sede Senac AM',
  },
  {
    name: 'Aula Magna da 3ª turma da Pós-Graduação em Gestão da Inovação com ênfase em PD&I',
    date: '2026',
    place: 'Centro de Informática',
  },
  {
    name: 'Open Innovation 2026 e 2ª Semana da Propriedade Intelectual',
    date: '28/04/26',
    place: 'Auditório da sede Senac AM',
  },
  {
    name: 'Colação de Grau da 2ª Turma da Pós-Graduação em Gestão da Inovação com ênfase em PD&I',
    date: '23/04/26',
    place: 'Auditório da sede Senac AM',
  },
  {
    name: 'I Conexão Empreendedora',
    date: '09/12/25',
    place: 'Unidade Pequeno Franco',
  },
  {
    name: 'INOVA+ Saúde: Inovação, Tecnologia e Pesquisa',
    date: '12/11/25',
    place: 'Auditório do Pequeno Franco',
  },
  {
    name: 'II Fórum Acadêmico: Computação Quântica',
    date: '23/05/25',
    place: 'Auditório da sede Senac AM',
  },
  {
    name: 'Aula Magna da 2ª turma da Pós-Graduação em Gestão da Inovação com ênfase em PD&I',
    date: '25/01/2025',
    place: 'Centro de Informática',
  },
  {
    name: 'Seminário de Inovação: Ideias que conectam, iniciativas que transformam',
    date: '05/08/25',
    place: 'Hotel Intercity Manaus',
  },
  {
    name: 'Visita Técnica ao Polo de Inovação do Instituto Federal do Amazonas - IFAM',
    date: '20/05/25',
    place: 'IFAM',
  },
  {
    name: 'Palestra "Projetos de PD&I para Bioeconomia, Novos Combustíveis e Economia Circular", com a Unidade Empresa Brasileira de Pesquisa e Inovação Industrial (EMBRAPII) Fibras Florestais (UFV)',
    date: '28/02/25',
    place: 'Auditório do Pequeno Franco',
  },
  {
    name: 'Talk de Inovação: Gestão e Captação de Recursos de PD&I, com a Emanuela Dias - Coordenadora da Softex Amazônia',
    date: '29/11/24',
    place: 'Auditório da sede Senac AM',
  },
  {
    name: 'Visita Técnica ao Instituto de Pesquisas Eldorado',
    date: '08/08/24',
    place: 'Instituto Eldorado',
  },
  {
    name: 'DemoDay de Projetos do Curso de Desenvolvedor Full Stack',
    date: '22/07/24',
    place: 'Centro de Informática',
  },
  {
    name: 'I Fórum Acadêmico do curso de Tecnologia em Análise e Desenvolvimento de Sistemas: Evolução das Plataformas de Nuvem',
    date: '01/12/2023',
    place: 'Online',
  },
  {
    name: 'Aula Magna da 1ª turma da Pós-Graduação em Gestão da Inovação com ênfase em PD&I',
    date: '11/11/2023',
    place: 'Centro de Informática',
  },
];
const governanceItems = [
  {
    title: 'Estrutura Organizacional',
    lines: [
      'O Instituto de Ciência e Tecnologia do Senac Amazonas (ICT Senac AM) possui uma estrutura de governança alinhada às diretrizes institucionais do Serviço Nacional de Aprendizagem Comercial, garantindo a gestão estratégica das atividades de pesquisa, desenvolvimento e inovação.',
    ],
  },
  {
    title: 'Conselho Nacional',
    lines: ['José Roberto Tadros - Presidente'],
  },
  {
    title: 'Conselho Regional Senac Amazonas',
    lines: ['Aderson Santos da Frota - Presidente em exercício do Conselho Regional Senac Amazonas'],
  },
  {
    title: 'Direção Regional Senac Amazonas',
    lines: ['Silvana Maria Ferreira de Carvalho - Diretora Regional do Senac Amazonas'],
  },
  {
    title: 'Faculdade de Tecnologia Senac Amazonas',
    lines: ['Instituto de Ciência e Tecnologia ICT-Senac AM', 'Nailson Andrade de Souza - Direção'],
  },
  {
    title: 'Núcleo de Ensino, Pesquisa e Extensão - NEPE',
    lines: ['Líder do Grupo de Pesquisas', 'Samara Castro da Silva - Coordenação'],
  },
  {
    title: 'Núcleo de Inovação Tecnológica, Trabalhabilidade e Empreendedorismo - NITE',
    lines: ['Núcleo de Apoio Psicopedagógico e Serviços de Acessibilidade - NAPSA', 'Elidioney Batista de Souza - Coordenação'],
  },
];

const policyParagraphs = [
  'A nossa política de inovação está formalizada e institucionalizada com base nas Diretrizes de Inovação do Senac Brasil, documento estruturante da Rede Senac de Inovação Aberta. Esse documento estabelece orientações estratégicas para a atuação integrada dos Departamentos Regionais e do Departamento Nacional na promoção da inovação, com foco na criação, implementação e disseminação de soluções inovadoras voltadas aos segmentos de comércio de bens, serviços e turismo.',
  'A política de inovação do Senac é baseada em um modelo de inovação aberta, que promove a interação entre instituições de ensino, setor produtivo, governo e sociedade, fortalecendo os ecossistemas de inovação locais e regionais. Esse modelo está estruturado na atuação sistêmica em rede, integrando diferentes unidades, como Instituições Científicas, Tecnológicas e de Inovação (ICTs), hubs de inovação e núcleos de pesquisa aplicada.',
  'Além disso, a política de inovação está alinhada ao planejamento estratégico do Senac, que estabelece a inovação como eixo estruturante para o fortalecimento da educação profissional e para a geração de impacto econômico e social.',
  'Dessa forma, a Faculdade de Tecnologia Senac Amazonas, em articulação com o ICT Senac/AM, opera em conformidade com as diretrizes nacionais de inovação da instituição, garantindo aderência ao Marco Legal da Ciência, Tecnologia e Inovação e às boas práticas de governança em inovação.',
];

const policyGuidelines = [
  'A promoção da cultura de inovação, com incentivo à experimentação, criatividade e aprendizagem contínua',
  'A integração entre ensino, pesquisa aplicada e extensão',
  'O desenvolvimento de soluções tecnológicas alinhadas às demandas do setor produtivo',
  'A articulação com empresas, startups e demais atores do ecossistema de inovação',
  'A geração e disseminação de conhecimento aplicado',
];

const policyLinks = [
  {
    label: 'Diretrizes de Inovação',
    href: 'http://www.extranet.senac.br/diretrizes-ia/index.html',
  },
  {
    label: 'Diretrizes de Inteligência Artificial',
    href: 'http://www.extranet.senac.br/diretrizes-ia/index.html',
  },
  {
    label: 'Portal Extranet para demais políticas e documentos',
    href: 'http://www.extranet.senac.br/extranet/documentos.html',
  },
];

const nitActions = [
  'Gestão da política institucional de inovação',
  'Promoção da cultura de inovação e empreendedorismo',
  'Apoio à criação, desenvolvimento e proteção de soluções tecnológicas',
  'Prospecção tecnológica e inteligência competitiva',
  'Apoio à transferência de tecnologia e interação com o setor produtivo',
  'Articulação com empresas, startups e instituições do ecossistema de inovação',
  'Apoio ao desenvolvimento de projetos de pesquisa aplicada com estudantes',
];

const symplaProducerUrl = 'https://www.sympla.com.br/produtor/faculdadesenacam';

const researchers = [
  {
    name: 'Prof. Dr. Anderson Clayton Barreto Rodrigues',
    lattes: 'http://lattes.cnpq.br/3532538302321820',
    bio:
      'Doutor em Informática (UFAM), com formação em Processamento de Dados e experiência em tecnologia da informação e desenvolvimento de sistemas. Atua como analista de TI na Secretaria de Estado da Fazenda do Amazonas - SEFAZ-AM e docente na Faculdade de Tecnologia Senac Amazonas.',
  },
  {
    name: 'Prof. Me. Carlos Bruno Oliveira Lopes',
    lattes: 'http://lattes.cnpq.br/8195773427038880',
    orcid: 'https://orcid.org/0000-0003-0747-0999',
    bio:
      'Mestre em Ciência da Computação, doutorando em Informática e bacharel em Engenharia da Computação, com atuação em processamento de imagens e visão computacional. Possui experiência no desenvolvimento de soluções baseadas em modelos matemáticos e aplicações em fotografia computacional.',
  },
  {
    name: 'Prof. MSc. José Carlos da Silva Duarte Filho',
    lattes: 'http://lattes.cnpq.br/1129223641756414',
    orcid: 'https://orcid.org/0000-0001-5732-9729',
    bio:
      'Doutorando e Mestre em Informática (ICOMP/UFAM). Licenciado em Informática (UEA). Áreas: Educação em Computação, Eng. de Software, UX e Learning Sciences. Docente Senac AM e voluntário UEA/EST.',
  },
  {
    name: 'Prof. Me. Edwins Moura Ramires',
    lattes: 'http://lattes.cnpq.br/7067138841768507',
    bio:
      'Mestre em Engenharia de Processos (UFPA) e especialista em redes, telecomunicações e ensino superior, com atuação em cursos de TI e formação profissional no Senac Amazonas. Professor universitário e coordenador de TI na SES-AM, com experiência em inovação tecnológica, redes, segurança da informação e desenvolvimento de projetos educacionais e tecnológicos.',
  },
  {
    name: 'Profa. Dra. Samara Castro da Silva',
    lattes: 'http://lattes.cnpq.br/3377154555824021',
    orcid: 'https://orcid.org/0000-0002-8261-8239',
    bio:
      'Doutora em Turismo e Hospitalidade (UCS), Mestre em Engenharia de Produção (UFAM), MBA em Pesquisa, Desenvolvimento e Inovação (UEA), MBA Executivo em Gestão de Organizações, Operações e Serviços (UFAM) e Especialista em Estudos Amazônicos (IFPA/USP). Administradora e Bacharel em Direito, atua nas áreas de pesquisa aplicada, empreendedorismo, transformação digital e desenvolvimento regional. É docente e Coordenadora do Núcleo de Ensino, Pesquisa e Extensão da Faculdade de Tecnologia Senac Amazonas, pesquisadora do Instituto de Ciência e Tecnologia do Senac Amazonas (ICT Senac AM) e do Centro de Referência em Inovação e Tecnologia (CRIT Senac AM). Coordena projetos de Pesquisa, Desenvolvimento e Inovação (PD&I) voltados à inteligência econômica, tecnologias digitais, inovação aberta e competitividade empresarial, além de liderar o grupo de pesquisa "Pesquisa, Desenvolvimento e Inovação em atividades do comércio de bens, serviços e turismo". Atualmente, preside a Rede de Inovação e Empreendedorismo da Amazônia (RAMI), iniciativa voltada ao fortalecimento de incubadoras, aceleradoras, parques tecnológicos e demais mecanismos de inovação, promovendo a articulação entre academia, setor produtivo, governo e sociedade para o desenvolvimento dos ecossistemas de inovação da Amazônia.',
  },
  {
    name: 'Profa. Me. Mayanna Emilia Tenório Vicente',
    lattes: 'https://lattes.cnpq.br/4401989421180573',
    orcid: 'https://orcid.org/0000-0002-4177-1595',
    bio:
      'Mestra em Ciências e Tecnologia para Recursos Amazônicos, com formação em Engenharia Química e atuação em pesquisa aplicada voltada à economia circular e aproveitamento de resíduos amazônicos. Docente da Faculdade Senac Amazonas, com experiência em processos laboratoriais, análises físico-químicas e soluções sustentáveis para o setor produtivo.',
  },
  {
    name: 'Prof. Me. Luiz Ricardo de Almeida e Silva',
    lattes: 'https://lattes.cnpq.br/1133717587650774',
    bio:
      'Mestrado em Educação (2003); Especialização em Educação e Trabalho (1992); Especialização em Neuropsicopedagogia e Novas Aprendizagens (2017).',
  },
  {
    name: 'Prof. Me. Nailson Andrade de Souza',
    lattes: 'http://lattes.cnpq.br/3328364467712901',
    orcid: 'https://orcid.org/0009-0007-2003-5664',
    bio:
      'Engenheiro, pesquisador e gestor acadêmico com mais de 15 anos de experiência na educação profissional, tecnológica e superior. Mestre em Engenharia de Produção, especialista em Tecnologia Educacional e atuante nas áreas de Pesquisa, Desenvolvimento e Inovação (PD&I), gestão da educação, portfólio de cursos, metodologias ativas e avaliação institucional. Atualmente exerce funções de liderança na Faculdade de Tecnologia Senac Amazonas e no ICT Senac AM, coordenando iniciativas de pesquisa aplicada, inovação e desenvolvimento tecnológico.',
  },
  {
    name: 'Profa. Me. Nívea Consuêlo Carvalho dos Santos',
    lattes: 'http://lattes.cnpq.br/4219608679457725',
    orcid: 'https://orcid.org/0009-0009-3817-4800',
    bio:
      'Mestra em Ensino Tecnológico (IFAM), com formação multidisciplinar em Administração, TI e Inovação, atuando como coordenadora e docente na Faculdade de Tecnologia Senac Amazonas. Possui experiência em projetos educacionais, inovação e transformação digital, com atuação em consultorias, pesquisa e mentoria em desenvolvimento de soluções tecnológicas.',
  },
  {
    name: 'Prof. Me. Ramiro Hitotuzi Gomes',
    lattes: 'https://lattes.cnpq.br/4473774038395055',
    orcid: 'https://orcid.org/0000-0001-8127-6882',
    bio:
      'Mestre em Sociedade e Cultura na Amazônia (UFAM), com pesquisa voltada às práticas alimentares amazônicas sob perspectivas socioculturais. Docente e pesquisador, atua em projetos socioeducacionais e escreve sobre alimentação, cultura e sociedade no Jornal do Commercio do Amazonas.',
  },
  {
    name: 'Profa. Dra. Tatiana da Costa Jansen',
    lattes: 'http://lattes.cnpq.br/3945931267553518',
    orcid: 'https://orcid.org/0009-0006-6715-0275',
    bio:
      'Doutora em Biotecnologia pela Universidade Federal do Amazonas (UFAM), Mestre em Genética e Evolução pela Universidade Federal de São Carlos (UFSCar), especialista em Biotecnologia e Bacharel em Direito. Atua em pesquisas voltadas à genética de microrganismos, biotecnologia, produção de bioativos, biodiversidade amazônica e inovação aplicada. Desenvolve estudos interdisciplinares envolvendo biotecnologia, propriedade intelectual e aspectos regulatórios da inovação tecnológica.',
  },
  {
    name: 'Profa. Me. Yadira Pérez Cutiño',
    lattes: 'http://lattes.cnpq.br/3385687956857088',
    orcid: 'https://orcid.org/0000-0002-3994-7352',
    bio:
      'Mestra em Administração pela Universidad Central Marta Abreu de Las Villas (Cuba), com diploma revalidado pela Universidade Federal de Minas Gerais (UFMG). Economista com experiência em desenvolvimento territorial, gestão da inovação, administração, auditoria e planejamento estratégico. Atuou em projetos de desenvolvimento local vinculados ao governo cubano e atualmente integra atividades acadêmicas e de pesquisa na Faculdade de Tecnologia Senac Amazonas, com interesse em gestão, inovação, educação e desenvolvimento regional.',
  },
  {
    name: 'Amanda Cristina Oliveira Mota Flores',
    bio:
      'Mestra em Ciências da Comunicação pela Universidade Federal do Amazonas (UFAM), especialista em Gestão de Talentos e em Produção de Textos. Atua nas áreas de comunicação, marketing digital, empreendedorismo, inovação e educação tecnológica. Integra projetos de capacitação tecnológica e inovação, com experiência em mídias digitais, gestão de projetos educacionais e desenvolvimento de iniciativas voltadas ao empreendedorismo tecnológico.',
  },
  {
    name: 'Prof. Me. Mateus Victor Almeida de Freitas',
    bio:
      'Mestrando em Educação Profissional e Tecnológica (ProfEPT/IFAM), graduado em Análise e Desenvolvimento de Sistemas e especialista em Engenharia de Software. Atua nas áreas de desenvolvimento de sistemas, inteligência artificial, banco de dados e tecnologias educacionais, desenvolvendo soluções voltadas à inovação, inclusão digital e transformação tecnológica aplicada à educação.',
  },
  {
    name: 'Profa. Dra. Sammy Aquino Pereira',
    bio:
      'Doutora em Agronomia Tropical pela Universidade Federal do Amazonas (UFAM), Mestre em Ciências de Florestas Tropicais (INPA) e Mestre em Propriedade Intelectual e Transferência de Tecnologia para Inovação (PROFNIT/UFRR), especialista em Gestão de Projetos e Engenheira Florestal. Atua nas áreas de Propriedade Intelectual, Inovação, Transferência de Tecnologia, Inteligência Competitiva, Prospecção Tecnológica e Gestão da Informação. Possui experiência na implantação e fortalecimento de Núcleos de Inovação Tecnológica (NITs), gestão de ativos de propriedade intelectual, elaboração de políticas institucionais de inovação e apoio à proteção de marcas, patentes e programas de computador. Desenvolve atividades relacionadas à biodiversidade amazônica, bioeconomia e transferência de tecnologia. Atualmente é Professora Pesquisadora Visitante do ICT Senac Amazonas, Professora Colaboradora do PROFNIT/UFAM e Vice-Presidente do Instituto de Propriedade Intelectual da Amazônia (IPIAM).',
  },
  {
    name: 'Profa. Me. Jessica Oliveira Janes',
    bio:
      'Mestranda em Design pela Universidade Federal do Amazonas (UFAM), especialista em Gestão da Inovação, Tecnologia e Empreendedorismo, Auditoria e Compliance e Negócios Inovadores da Amazônia. Atua nas áreas de inovação, empreendedorismo, propriedade intelectual, desenvolvimento de negócios e gestão de projetos, integrando grupos de pesquisa voltados à inovação e transferência de tecnologia.',
  },
];
const SectionHeading: React.FC<{ children: React.ReactNode; eyebrow?: string }> = ({ children, eyebrow }) => (
  <Box sx={{ mb: { xs: 2.4, md: 3 } }}>
    {eyebrow ? (
      <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800, letterSpacing: 1 }}>
        {eyebrow}
      </Typography>
    ) : null}
    <Typography
      variant="h5"
      sx={{
        color: 'primary.main',
        fontWeight: 800,
        lineHeight: 1.25,
        fontSize: { xs: '1.28rem', sm: '1.45rem', md: '1.5rem' },
      }}
    >
      {children}
    </Typography>
  </Box>
);

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: alpha(theme.palette.primary.main, 0.1),
        boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
        <Stack direction="row" spacing={1.4} alignItems="flex-start">
          <Box
            sx={{
              width: 38,
              height: 38,
              flex: '0 0 auto',
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              color: 'secondary.main',
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: 'primary.main', fontWeight: 800, fontSize: { xs: '0.98rem', md: '1rem' } }}
            >
              {title}
            </Typography>
            <Typography component="div" sx={{ mt: 0.8, color: 'text.secondary', lineHeight: 1.7 }}>
              {children}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ResearcherCard: React.FC<{ name: string; lattes?: string; bio: string; orcid?: string }> = ({
  name,
  lattes,
  bio,
  orcid,
}) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: alpha(theme.palette.primary.main, 0.1),
        boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, fontSize: '1rem' }}>
          {name}
        </Typography>
        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mt: 0.4, minHeight: 30 }}>
          {lattes ? (
            <Button
              size="small"
              variant="text"
              color="primary"
              endIcon={<Launch fontSize="small" />}
              href={lattes}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ px: 0, fontWeight: 800 }}
            >
              Currículo Lattes
            </Button>
          ) : (
            <Chip label="Dados em atualização" size="small" variant="outlined" />
          )}
          {orcid ? (
            <Button
              size="small"
              variant="text"
              color="secondary"
              endIcon={<Launch fontSize="small" />}
              href={orcid}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ px: 0, fontWeight: 800 }}
            >
              Orcid
            </Button>
          ) : null}
        </Stack>
        <Typography sx={{ mt: 1.4, color: 'text.secondary', lineHeight: 1.75 }}>{bio}</Typography>
      </CardContent>
    </Card>
  );
};

const ICT: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<IctTab>('sobre');

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (tabIds.includes(hash as IctTab)) {
      setActiveTab(hash as IctTab);
    }
  }, [location.hash]);

  const selectTab = (tab: IctTab) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  const underline = {
    content: '""',
    position: 'absolute' as const,
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    width: { xs: 150, md: 240 },
    height: 4,
    borderRadius: 99,
    backgroundColor: theme.palette.secondary.main,
  };

  const renderGovernance = () => (
    <>
      <SectionHeading eyebrow="Governança">Governança</SectionHeading>
      <Stack spacing={2.2}>
        {governanceItems.map((item) => (
          <Box key={item.title}>
            <Typography sx={{ color: 'primary.main', fontWeight: 800, lineHeight: 1.4 }}>{item.title}</Typography>
            {item.lines.map((line) => (
              <Typography key={line} sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                {line}
              </Typography>
            ))}
          </Box>
        ))}
      </Stack>
    </>
  );

  const renderPolicies = () => (
    <>
      <SectionHeading eyebrow="Diretrizes">Políticas Institucionais de Pesquisa e Inovação</SectionHeading>
      {policyParagraphs.slice(0, 2).map((paragraph) => (
        <Typography
          key={paragraph}
          paragraph
          sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}
        >
          {paragraph}
        </Typography>
      ))}

      <Grid container spacing={2.2} sx={{ my: 1 }}>
        {policyLinks.map((item) => (
          <Grid item xs={12} md={4} key={item.label}>
            <SummaryCard icon={<Description />} title={item.label}>
              <Button
                size="small"
                variant="text"
                endIcon={<Launch fontSize="small" />}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ px: 0, mt: 0.4, fontWeight: 800 }}
              >
                Acessar
              </Button>
            </SummaryCard>
          </Grid>
        ))}
      </Grid>

      <Card variant="outlined" sx={{ mt: 2.2, borderColor: alpha(theme.palette.primary.main, 0.1) }}>
        <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 800 }}>
            No âmbito institucional, a política orienta:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.2, color: 'text.secondary', lineHeight: 1.7 }}>
            {policyGuidelines.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Stack spacing={1.4} sx={{ mt: 2.2 }}>
        {policyParagraphs.slice(2).map((paragraph) => (
          <Typography key={paragraph} sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}>
            {paragraph}
          </Typography>
        ))}
      </Stack>
    </>
  );

  const renderNit = () => (
    <>
      <SectionHeading eyebrow="Núcleo de Inovação Tecnológica">NIT</SectionHeading>
      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, fontSize: '1rem', mb: 1 }}>
        Núcleo de Inovação Tecnológica (NIT)
      </Typography>
      <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}>
        O Núcleo de Inovação Tecnológica (NIT) vinculado à Faculdade de Tecnologia Senac Amazonas é estruturado por
        meio do Instituto de Ciência e Tecnologia - ICT Senac/AM, que atua como unidade responsável pela gestão da
        inovação, pesquisa aplicada e desenvolvimento tecnológico no âmbito institucional.
      </Typography>
      <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}>
        O NIT atua em conformidade com a Lei nº 10.973/2004 e suas atualizações, exercendo suas competências no apoio à
        inovação e à geração de soluções com potencial de impacto econômico e social.
      </Typography>

      <SummaryCard icon={<Hub />} title="Principais ações desenvolvidas pelo NIT">
        <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.2 }}>
          {nitActions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </Box>
      </SummaryCard>
    </>
  );

  const renderResearchGroups = () => (
    <>
      <SectionHeading eyebrow="Grupo de pesquisa">Grupos e Linhas de Pesquisa</SectionHeading>
      <SummaryCard icon={<Hub />} title="Pesquisa, Desenvolvimento e Inovação">
        {researchGroupName}
      </SummaryCard>

      <Stack spacing={2.2} sx={{ mt: 2.2 }}>
        {researchLines.map((line) => (
          <Card
            key={line.title}
            variant="outlined"
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.1),
              boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ mb: 1.6 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    flex: '0 0 auto',
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'secondary.main',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  }}
                >
                  <Science fontSize="small" />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ color: 'primary.main', fontWeight: 800, fontSize: { xs: '1rem', md: '1.05rem' } }}
                >
                  {line.title}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                    Descrição
                  </Typography>
                  <Typography sx={{ mt: 0.6, color: 'text.secondary', lineHeight: 1.8, textAlign: 'justify' }}>
                    {line.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 1.6,
                      borderLeft: { xs: 0, md: `3px solid ${alpha(theme.palette.secondary.main, 0.35)}` },
                      borderTop: { xs: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, md: 0 },
                      bgcolor: alpha(theme.palette.secondary.main, 0.04),
                    }}
                  >
                    <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                      Objetivo
                    </Typography>
                    <Typography sx={{ mt: 0.6, color: 'text.secondary', lineHeight: 1.75 }}>
                      {line.objective}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </>
  );

  const renderLaboratories = () => (
    <>
      <SectionHeading eyebrow="Infraestrutura de PD&I">Infraestrutura e Laboratórios</SectionHeading>
      {infrastructureIntro.map((paragraph) => (
        <Typography
          key={paragraph}
          paragraph
          sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}
        >
          {paragraph}
        </Typography>
      ))}

      <Grid container spacing={2.2} sx={{ mt: 0.5 }}>
        {laboratoryEnvironments.map((environment) => (
          <Grid item xs={12} md={6} key={environment.title}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: alpha(theme.palette.primary.main, 0.1),
                boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, fontSize: '1rem' }}>
                  {environment.title}
                </Typography>
                <Typography sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.75 }}>
                  {environment.description}
                </Typography>
                {environment.resources?.length ? (
                  <Box sx={{ mt: 1.6 }}>
                    <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                      Recursos disponíveis
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.6, mb: 0, pl: 2.2, color: 'text.secondary', lineHeight: 1.65 }}>
                      {environment.resources.map((resource) => (
                        <li key={resource}>{resource}</li>
                      ))}
                    </Box>
                  </Box>
                ) : null}
                {environment.applications?.length ? (
                  <Box sx={{ mt: 1.6 }}>
                    <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                      {environment.applicationsTitle || 'Aplicações'}
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.6, mb: 0, pl: 2.2, color: 'text.secondary', lineHeight: 1.65 }}>
                      {environment.applications.map((application) => (
                        <li key={application}>{application}</li>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  const renderProjects = () => (
    <>
      <SectionHeading eyebrow="Projetos">Projetos de Pesquisa em Desenvolvimento Tecnológico</SectionHeading>
      <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}>
        Relação de projetos de pesquisa aplicada, desenvolvimento tecnológico e desenvolvimento experimental vinculados
        ao ICT Senac AM, com foco em transformação digital, inteligência artificial, ciência de dados, IoT e inovação para
        os setores de comércio, serviços e turismo.
      </Typography>

      <Stack spacing={2.2}>
        {technologyProjects.map((project) => (
          <Card
            key={project.title}
            variant="outlined"
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.1),
              boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ mb: 1.8 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flex: '0 0 auto',
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'secondary.main',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  }}
                >
                  <Inventory2 fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 800,
                      fontSize: { xs: '1rem', md: '1.08rem' },
                      lineHeight: 1.35,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {project.title}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip label={`Fomento: ${project.funding}`} size="small" color="primary" variant="outlined" />
                    <Chip label={`Edital: ${project.callNumber}`} size="small" variant="outlined" />
                    <Chip
                      label={project.supportMode}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{
                        height: 'auto',
                        maxWidth: '100%',
                        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.35 },
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>

              <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                Objetivo Geral
              </Typography>
              <Typography sx={{ mt: 0.6, color: 'text.secondary', lineHeight: 1.8, textAlign: 'justify' }}>
                {project.objective}
              </Typography>

              <Grid container spacing={2.2} sx={{ mt: 0.4 }}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1.1}>
                    {[
                      ['Instituição Proponente', project.institution],
                      ['Grupo de Pesquisa', project.group],
                      ['Linha de Pesquisa', project.line],
                      ['Coordenadora', project.coordinator],
                    ].map(([label, value]) => (
                      <Box key={label}>
                        <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.9rem' }}>
                          {label}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{value}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.9rem' }}>
                    Pesquisadores
                  </Typography>
                  <Typography sx={{ mt: 0.4, color: 'text.secondary', lineHeight: 1.7 }}>
                    {project.researchers.join('; ')}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2.2} sx={{ mt: 0.6 }}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                    Tecnologias Prioritárias
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {project.technologies.map((technology) => (
                      <Chip
                        key={technology}
                        label={technology}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 'auto',
                          maxWidth: '100%',
                          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.35 },
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.95rem' }}>
                    Resultados Tecnológicos Esperados
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.8, mb: 0, pl: 2.2, color: 'text.secondary', lineHeight: 1.65 }}>
                    {project.expectedResults.map((result) => (
                      <li key={result}>{result}</li>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </>
  );

  const renderPublications = () => (
    <>
      <SectionHeading eyebrow="Produção acadêmica">Publicações PD&I</SectionHeading>
      <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}>
        E-books publicados no âmbito do Programa de Pós graduação lato sensu em Gestão da Inovação com ênfase em
        Pesquisa, Desenvolvimento e Inovação, reunindo produção técnica e acadêmica relacionada a PD&I.
      </Typography>

      <Grid container spacing={2.2}>
        {pdiPublications.map((publication) => (
          <Grid item xs={12} md={6} key={publication.title}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: alpha(theme.palette.primary.main, 0.1),
                boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                  <Box
                    sx={{
                      width: { xs: 'min(100%, 220px)', sm: 150, md: 170 },
                      flex: '0 0 auto',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    }}
                  >
                    <Box
                      component="img"
                      src={publication.cover}
                      alt={`Capa do e-book ${publication.title}`}
                      sx={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                  </Box>

                  <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <Chip label={publication.type} size="small" color="primary" variant="outlined" />
                      {publication.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 800,
                        fontSize: { xs: '1rem', md: '1.05rem' },
                        lineHeight: 1.35,
                      }}
                    >
                      {publication.title}
                    </Typography>
                    <Typography sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.75 }}>
                      {publication.description}
                    </Typography>

                    {publication.organizers?.length ? (
                      <Box sx={{ mt: 1.4 }}>
                        <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.9rem' }}>
                          Organizadores
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                          {publication.organizers.join('; ')}
                        </Typography>
                      </Box>
                    ) : null}

                    <Stack spacing={0.4} sx={{ mt: 1.4 }}>
                      {publication.doi ? (
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                            DOI:
                          </Box>{' '}
                          {publication.doi}
                        </Typography>
                      ) : null}
                      {publication.isbn ? (
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                            ISBN:
                          </Box>{' '}
                          {publication.isbn}
                        </Typography>
                      ) : null}
                    </Stack>

                    {publication.url ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        endIcon={<Launch fontSize="small" />}
                        href={publication.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mt: 1.6, fontWeight: 800 }}
                      >
                        Acessar publicação
                      </Button>
                    ) : null}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  const renderEventsAgenda = () => (
    <>
      <SectionHeading eyebrow="Eventos">Agenda e Eventos</SectionHeading>
      <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
        Espaço para divulgação de eventos, encontros e atividades relacionadas à pesquisa e inovação.
      </Typography>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
        <Box component="table" sx={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', bgcolor: '#fff' }}>
          <Box component="thead" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
            <Box component="tr">
              {['Eventos com foco em PD&I', 'Data', 'Local'].map((heading) => (
                <Box
                  key={heading}
                  component="th"
                  sx={{
                    p: 1.4,
                    textAlign: 'left',
                    color: 'primary.main',
                    fontWeight: 800,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    whiteSpace: heading === 'Data' ? 'nowrap' : 'normal',
                  }}
                >
                  {heading}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {eventsAgenda.map((event) => (
              <Box component="tr" key={`${event.name}-${event.date}`}>
                <Box
                  component="td"
                  sx={{ p: 1.4, color: 'text.primary', borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`, lineHeight: 1.5 }}
                >
                  {event.name}
                </Box>
                <Box
                  component="td"
                  sx={{ p: 1.4, color: 'text.secondary', borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`, whiteSpace: 'nowrap' }}
                >
                  {event.date}
                </Box>
                <Box
                  component="td"
                  sx={{ p: 1.4, color: 'text.secondary', borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}` }}
                >
                  {event.place}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderEventRegistration = () => (
    <>
      <SectionHeading eyebrow="Eventos">Inscrições e Informações</SectionHeading>
      <Card variant="outlined" sx={{ borderColor: alpha(theme.palette.primary.main, 0.1) }}>
        <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
            Os eventos divulgados pela Faculdade Senac Amazonas também podem ser acompanhados pelo canal oficial no
            Sympla.
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            endIcon={<Launch fontSize="small" />}
            href={symplaProducerUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 1.5, fontWeight: 800 }}
          >
            Acessar eventos no Sympla
          </Button>
        </CardContent>
      </Card>
    </>
  );

  const renderEvents = () => renderEventsAgenda();

  const renderResearchers = () => (
    <>
      <SectionHeading eyebrow="Corpo de Pesquisadores">Pesquisadores</SectionHeading>
      <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}>
        O Instituto de Ciência e Tecnologia do Senac Amazonas conta com pesquisadores vinculados às áreas de Inteligência
        Artificial, Ciência de Dados, Transformação Digital, Desenvolvimento de Software, Internet das Coisas, Saúde Digital
        e Inteligência Econômica.
      </Typography>
      <Grid container spacing={2.2}>
        {researchers.map((researcher) => (
          <Grid item xs={12} md={6} key={researcher.name}>
            <ResearcherCard
              name={researcher.name}
              lattes={researcher.lattes}
              bio={researcher.bio}
              orcid={researcher.orcid}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );

  const renderAbout = () => (
    <>
      <SectionHeading eyebrow="Apresentação institucional">Sobre</SectionHeading>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.6 }}>
        <Chip label="ICT Senac AM" color="primary" variant="outlined" />
        <Chip label="Pesquisa, Desenvolvimento e Inovação" color="secondary" variant="outlined" />
        <Chip label="Transferência de conhecimento" variant="outlined" />
      </Stack>

      {aboutParagraphs.map((paragraph) => (
        <Typography
          key={paragraph}
          paragraph
          sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify' }}
        >
          {paragraph}
        </Typography>
      ))}

      <Grid container spacing={2.2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <SummaryCard icon={<ManageSearch />} title="Missão">
            Inovar para fortalecer o setor de comércio, bens, serviços e turismo, desenvolvendo soluções tecnológicas e
            talentos para a transformação digital no Amazonas.
          </SummaryCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SummaryCard icon={<Hub />} title="Visão">
            Ser reconhecido por conectar conhecimento, tecnologia e desenvolvimento humano no segmento do comércio de
            bens, serviços e turismo.
          </SummaryCard>
        </Grid>
        <Grid item xs={12}>
          <SummaryCard icon={<Groups />} title="Valores">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {values.map((value) => (
                <Chip key={value} label={value} size="small" variant="outlined" />
              ))}
            </Stack>
          </SummaryCard>
        </Grid>
        <Grid item xs={12}>
          <SummaryCard icon={<BusinessCenter />} title="Objetivos">
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.2, color: 'text.secondary', lineHeight: 1.7 }}>
              {objectives.map((objective) => (
                <li key={objective}>{objective}</li>
              ))}
            </Box>
          </SummaryCard>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3.2 }}>
        <SectionHeading eyebrow="Trajetória institucional">Histórico</SectionHeading>
        <Card variant="outlined" sx={{ borderColor: alpha(theme.palette.primary.main, 0.1) }}>
          <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
            {historyParagraphs.map((paragraph) => (
              <Typography
                key={paragraph}
                paragraph
                sx={{ color: 'text.secondary', lineHeight: 1.85, textAlign: 'justify', '&:last-child': { mb: 0 } }}
              >
                {paragraph}
              </Typography>
            ))}
          </CardContent>
        </Card>
      </Box>
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'sobre':
        return renderAbout();
      case 'governanca':
        return renderGovernance();
      case 'politicas':
        return renderPolicies();
      case 'grupos-linhas':
        return renderResearchGroups();
      case 'pesquisadores':
        return renderResearchers();
      case 'laboratorios':
        return renderLaboratories();
      case 'projetos':
        return renderProjects();
      case 'nit':
        return renderNit();
      case 'publicacoes-pdi':
        return renderPublications();
      case 'eventos':
      case 'agenda-eventos':
        return renderEvents();
      case 'inscricoes-informacoes':
        return renderEventRegistration();
      default:
        return renderAbout();
    }
  };
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 6, lg: 8 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            letterSpacing: 0.2,
            display: 'inline-block',
            pb: 1.5,
            position: 'relative',
            color: 'primary.main',
            fontSize: { xs: '1.7rem', sm: '2rem', md: '2.125rem' },
            '&::after': underline,
          }}
        >
          ICT Senac-AM
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2.5, md: 3 }}>
        <Grid item xs={12} md={2.7} lg={2.5}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', md: 'column' },
              gap: { xs: 0.8, md: 0.9 },
              overflowX: { xs: 'auto', md: 'visible' },
              pb: { xs: 1, md: 0 },
              mx: { xs: -1.5, sm: -2.5, md: 0 },
              px: { xs: 1.5, sm: 2.5, md: 0 },
              scrollSnapType: { xs: 'x mandatory', md: 'none' },
              WebkitOverflowScrolling: 'touch',
              position: { md: 'sticky' },
              top: { md: 92 },
            }}
          >
            {navigationItems.map((tab) => {
              const selected = activeTab === tab.id;

              return (
                <Button
                  key={tab.id}
                  variant={selected ? 'contained' : 'outlined'}
                  onClick={() => selectTab(tab.id)}
                  fullWidth={!isMobile}
                  sx={{
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    ml: { xs: 0, md: tab.depth * 1.35 },
                    minWidth: { xs: 146, sm: 176, md: 'auto' },
                    py: 0.95,
                    px: 1.35,
                    textAlign: 'left',
                    textTransform: 'none',
                    fontWeight: tab.depth > 0 ? 700 : 800,
                    fontSize: { xs: '0.78rem', sm: tab.depth > 0 ? '0.8rem' : '0.86rem' },
                    lineHeight: 1.25,
                    scrollSnapAlign: 'start',
                    borderRadius: 1.4,
                    boxShadow: selected ? '0 8px 18px rgba(0, 58, 112, 0.16)' : 'none',
                    bgcolor: selected ? 'primary.main' : '#fff',
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Box>
        </Grid>

        <Grid item xs={12} md={9.3} lg={9.5}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 4 },
              borderRadius: { xs: 1.5, md: 2 },
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              boxShadow: '0 18px 42px rgba(15, 23, 42, 0.07)',
              bgcolor: '#fff',
              minHeight: { xs: 'auto', md: 480 },
            }}
          >
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ICT;
